<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-6">
					<h3 class="page-header">
						<button class="btn btn-refresh" @click="goBack" title="Back to daily attendance">
							<i class="fa fa-arrow-left"></i>
						</button>
						{{ (data && data.employee && data.employee.name) || 'Employee' }}
						<small class="text-muted" v-if="data && data.employee">
							{{ data.employee.code || '' }}
						</small>
					</h3>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-4">
					<label>Month</label>
					<div class="flex-row" style="display:flex;align-items:center">
						<button class="btn btn-default" @click="shiftMonth(-1)">
							<i class="fa fa-chevron-left"></i>
						</button>
						<input
							type="month"
							class="form-control form-cascade-control input-box-short"
							style="margin:0 8px"
							v-model="month"
							@change="refresh"
						/>
						<button class="btn btn-default" @click="shiftMonth(1)">
							<i class="fa fa-chevron-right"></i>
						</button>
					</div>
				</div>
				<div class="col-md-8 text-right">
					<label class="d-block">&nbsp;</label>
					<button class="btn btn-primary" :disabled="recomputing" @click="recomputeMonth">
						<i class="fa fa-refresh" :class="{ 'fa-spin': recomputing }"></i> Recompute month
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="summary" style="margin-top:10px">
			<span class="badge bg-success totals-badge">Present {{ summary.present }}</span>
			<span class="badge bg-warning text-dark totals-badge">Half day {{ summary.half_day }}</span>
			<span class="badge bg-danger totals-badge">Absent {{ summary.absent }}</span>
			<span class="badge bg-info text-dark totals-badge">Leave {{ summary.on_leave }}</span>
			<span class="badge bg-warning text-dark totals-badge">Late marks {{ summary.late }}</span>
			<span class="badge bg-dark totals-badge">OT {{ minutesToHours(summary.ot_minutes) }}</span>
			<span class="badge bg-dark totals-badge">Payable days {{ summary.payable_days }}</span>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th style="width:3rem"></th>
							<th>Date</th>
							<th>Status</th>
							<th>In</th>
							<th>Out</th>
							<th>Worked</th>
							<th>OT</th>
							<th>Payable</th>
							<th>Notes</th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="9" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!days.length">
								<td colspan="9" class="text-center">No data for this month.</td>
							</tr>
							<template v-for="day in days">
								<tr :key="day.work_date">
									<td>
										<button
											class="btn btn-sm btn-default"
											@click="toggle(day.work_date)"
											v-if="day.punches && day.punches.length"
										>
											<i
												class="fa"
												:class="expanded[day.work_date] ? 'fa-chevron-down' : 'fa-chevron-right'"
											></i>
										</button>
									</td>
									<td>
										<strong>{{ formatDate(day.work_date) }}</strong>
									</td>
									<td>
										<span :class="statusBadgeClass(day.status)">{{ statusLabel(day.status) }}</span>
									</td>
									<td>
										<span :class="{ 'text-warning font-weight-bold': day.is_late }">
											{{ day.first_in || '—' }}
										</span>
									</td>
									<td>{{ day.last_out || '—' }}</td>
									<td>{{ minutesToHours(day.effective_minutes) }}</td>
									<td>{{ minutesToHours(day.ot_minutes) }}</td>
									<td>{{ day.payable_factor }}</td>
									<td>
										<small v-if="day.exception_notes && day.exception_notes.length" class="text-warning">
											{{ day.exception_notes.join('; ') }}
										</small>
										<span v-else class="text-muted">—</span>
									</td>
								</tr>
								<!-- The punches behind the verdict, so a disputed day can be checked. -->
								<tr v-if="expanded[day.work_date]" :key="day.work_date + '-exp'">
									<td colspan="9" style="background:#f7f9fc">
										<table class="table table-sm" style="margin-bottom:0">
											<thead class="table-font">
												<th>Time</th>
												<th>Direction</th>
												<th>Source</th>
												<th>Location</th>
												<th>Fence</th>
												<th>Status</th>
												<th>Note</th>
											</thead>
											<tbody>
												<tr v-for="punch in day.punches" :key="punch.id">
													<td>{{ formatDateTime(punch.punched_at) }}</td>
													<td>
														<span
															class="badge"
															:class="punch.direction === 'in' ? 'bg-success' : 'bg-secondary'"
														>
															{{ punch.direction === 'in' ? 'In' : 'Out' }}
														</span>
													</td>
													<td>{{ punch.source }}</td>
													<td>{{ punch.address || '—' }}</td>
													<td>
														<span v-if="punch.within_fence === true" class="text-success">Inside</span>
														<span v-else-if="punch.within_fence === false" class="text-danger">
															{{ punch.distance_m ? punch.distance_m + 'm outside' : 'Outside' }}
														</span>
														<span v-else class="text-muted">Not enforced</span>
													</td>
													<td>{{ punch.status }}</td>
													<td class="text-muted">{{ punch.note || '—' }}</td>
												</tr>
											</tbody>
										</table>
									</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { registerService, errorMessage } from './attendanceService'
import attendanceFormat from './attendanceFormat'

export default {
	name: 'AttendanceEmployeeMonth',
	mixins: [attendanceFormat],
	data() {
		return {
			employeeId: Number(this.$route.params.employeeId),
			month: String(this.$route.query.month || this.currentMonth()),
			data: null,
			loading: false,
			error: '',
			recomputing: false,
			expanded: {},
		}
	},
	computed: {
		days() {
			return (this.data && this.data.days) || []
		},
		summary() {
			return this.data && this.data.summary
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		refresh() {
			this.loading = true
			this.error = ''
			this.expanded = {}
			registerService
				.employeeMonth(this.employeeId, this.month)
				.then((data) => {
					this.data = data
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load this month.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		/** Step the month picker without pulling in a full date component. */
		shiftMonth(delta) {
			const parts = this.month.split('-').map(Number)
			const date = new Date(parts[0], parts[1] - 1 + delta, 1)
			this.month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
			this.refresh()
		},
		recomputeMonth() {
			this.recomputing = true
			const parts = this.month.split('-').map(Number)
			const from = `${this.month}-01`
			const to = new Date(parts[0], parts[1], 0)
			const toIso = `${this.month}-${String(to.getDate()).padStart(2, '0')}`
			registerService
				.recompute({ from_date: from, to_date: toIso, employee_id: this.employeeId })
				.then((result) => {
					this.$notify({ text: `Recomputed: ${result.days} day(s) rebuilt.`, type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Recompute failed'), type: 'error' })
				})
				.finally(() => {
					this.recomputing = false
				})
		},
		toggle(workDate) {
			this.$set(this.expanded, workDate, !this.expanded[workDate])
		},
		goBack() {
			this.$router.push({ name: 'attendance-daily' })
		},
	},
}
</script>

<style scoped>
.totals-badge {
	margin-right: 6px;
	margin-bottom: 6px;
	display: inline-block;
	font-size: 12px;
}
</style>
